import { 
    AfterContentChecked
    , AfterViewInit
    , ChangeDetectorRef
    , Component
    , ElementRef
    , EventEmitter
    , HostListener
    , Input
    , OnChanges, OnInit
    , Output
    , Renderer2
    , signal, ViewChild 
} from '@angular/core';
import SignaturePad from 'signature_pad';
import { UntypedFormGroup } from '@angular/forms';
import { faFileSignature, faHouseMedicalCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { faUserNurse } from '@fortawesome/pro-duotone-svg-icons';
import { HttpClient } from '@angular/common/http';

import { SecurityService } from '../../../core/security/security.service';
import { profileIsOperational } from '../../../core/security/permission-profiles';
import { UserJsVm } from '../../generated/Admin/Models/User/UserJsVm';
import { InventoryActionEnum } from '../../../core/enums/generated/InventoryActionEnum';
import { NumberOfVerificationsEnum } from '../../../core/enums/generated/NumberOfVerificationsEnum';
import { VerificationTypeEnum } from '../../../core/enums/generated/VerificationTypeEnum';
import { SystemMessage, SystemMessageService } from '../../../core/system-message/system-message-service';
import { GlobalService } from '../../service/global.service';
import { FormatHelperService } from '../../helpers/format-helper.service';
import { SelectItem } from '../../models/select-item';
import { NotificationService } from '../../../services/notification.service';
import { GlobalVariablesService } from '../../../services/global-variables.service';

/** The model to include on pages to use the component */
export class DualAuthModel {
	newSignatureNumber?: number; // 1 for left signature, 2 for right
	pin?: string; // Used for pin verification
	oosName?: string; // Used for Out of Service verification 
	oosIsOffGoing!: boolean; // Used to determine if the Out of Service verification is on coming of off going
	userSignatureId?: number;
	verificationSettings!: VerificationSettings;
	signature1!: SignatureInput;
	signature2!: SignatureInput;
	isCombined?: boolean;
	isPending?: boolean;
	verificationType?: VerificationTypeEnum;
	signaturesComplete?: boolean;
}

/** Maps to the VerificationSetting entity, but is not automatically updated with the code gen */
export class VerificationSettings {
	numberOfVerifications!: NumberOfVerificationsEnum;
	verificationTypes!: VerificationTypeEnum[];
	inventoryAction?: InventoryActionEnum;
}

/** Event object to be returned on the DualAuthComponent signatureEvents output parameter */
export class SignatureEvent {
	signatureNumber!: number; // 1 for left signature, 2 for right
	signature!: SignatureInput;
	revert!: () => void;
}

/** Maps to the SignatureVm in the backend VerificationService, but is not automatically updated with the code gen */
export class SignatureInput {
	signatureId?: number = 0;
	signatureBase64!: string;
	signaturePath!: string;
	timeStamp?: Date;
	userId!: number;
	userName!: string;
	isPin!: boolean;
	isNuv!: boolean;
	isFinalized?: boolean;
	verificationType!: VerificationTypeEnum;
	isOos!: boolean;
	userSignatureId?: number;
}

/**
 * Component that supports both signature and FIDO authentication
 * HOW TO USE:
 * 	This component accepts a DualAuthModel object
 * 	The values emitted by the output must be handled within the parent component. Look to shift-change-edit.component.ts for an example
 *	The classes above should be imported into your component and input/output should be done through those types
*/
@Component({
    selector: 'pcg-double-auth'
    , templateUrl: './double-auth.component.html'
    , styleUrls: ['./double-auth.component.scss']
    , standalone: false
})
export class DoubleAuthComponent implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {

    // #region Get signature pad HTML references
    @ViewChild('container1') container1!: ElementRef;
    @ViewChild('container2') container2!: ElementRef;
	@ViewChild('canvas1') canvas1!: ElementRef;
	@ViewChild('canvas2') canvas2!: ElementRef;

	@ViewChild('nuvContainer1') nuvContainer1!: ElementRef;
    @ViewChild('nuvContainer2') nuvContainer2!: ElementRef;
	@ViewChild('nuvCanvas1') nuvCanvas1!: ElementRef;
	@ViewChild('nuvCanvas2') nuvCanvas2!: ElementRef;

	@ViewChild('txtPin1', { static: true }) txtPin1!: ElementRef;
	@ViewChild('txtPin2', { static: true }) txtPin2!: ElementRef;

	@ViewChild('txtNonUserName1', { static: false }) txtNonUserName1!: ElementRef;
	@ViewChild('txtNonUserName2', { static: false }) txtNonUserName2!: ElementRef;
	// #endregion

	// #region Inputs/Outputs
	@Input() model!: DualAuthModel;
	@Input() canEdit!: boolean;
	@Input() form!: UntypedFormGroup;
	@Input() saveUrl!: string;
	@Input() appendTitle = ['', ''];
	@Input() nuvString = "NUV";
	@Input() isMedUse = false;
	/**
	 * S2N-1420 (5c): whether this caller may fill the FIRST verification. The second is never
	 * gated by it -- on the medication-use workflow slot 2 is the designated witness position, and
	 * signing it is the one write a Witness is here to make ([AllowWitnessFinalize] on
	 * MedUseController.FinalizeMedUse).
	 *
	 * A separate flag rather than the existing `canEdit`, which is bound to record state.
	 *
	 * Not an authorization boundary: SignatureSlotMap.PrimaryWithWitness refuses a Witness PIN in
	 * slot 1 server-side, and CheckWitnessFinalizeAsync refuses a submission carrying a first
	 * signature at all. This only stops the client from offering what the server will refuse.
	 */
	@Input() canSignFirst = true;
	@Input() isWitness = false;
	/**
	 * S2N-1420: the pads a Witness-profile PIN may satisfy — the client mirror of the workflow's
	 * server-side SignatureSlotMap, which is what actually decides. Defaults to what the older
	 * flags imply: both pads when isWitness, pad 2 when isMedUse, none otherwise. Reconciliation,
	 * whose second position is a witness, passes [2].
	 */
	@Input() witnessEligiblePads: number[] | null = null;
	@Input() oosIsOffGoing = false;
	@Input() oosOnComingStr!: string;
	@Input() oosOffGoingStr!: string;
	@Input() oosTimeStamp!: Date;
	@Input() isUsingOos = false;
	@Input() singleAuthIsOpen = false;
	@Input() clientOutOfServiceName = "Out of Service";
    @Input() showNuvVerification = true;
    @Input() requireVerificationsInOrder = false;

	@Output() signatureEvents = new EventEmitter<SignatureEvent>();
	@Output() saveReturn = new EventEmitter<SystemMessage>();
    @Output() unsuccessful = new EventEmitter<SystemMessage>();
	@Output() beforeSave = new EventEmitter();
	@Output() doubleAuthIsOpen = new EventEmitter<boolean>();
	// #endregion

	// #region strings
    pin1!: string;
    pin2!: string;
	nonUserName1!: string;
    nonUserName2!: string;
	clientNonUserName!: string;
	// #endregion

	oosSelectItems!: SelectItem[];
	oosId!: number;

	// #region Signature pad objects
	signaturePad1!: SignaturePad;
	signaturePad2!: SignaturePad;

	nuvSignaturePad1!: SignaturePad;
	nuvSignaturePad2!: SignaturePad;
	// #endregion

	// These enums are declared here for use in the HTML
	verificationTypeSettingEnum = VerificationTypeEnum;
	NumberOfVerificationsEnum = NumberOfVerificationsEnum;

	user!: UserJsVm;
	clientId!: number;

	// #region booleans
	viewCheckInit = signal<boolean>(false); // Boolean to control the ngAfterContentChecked
    isSig1 = signal<boolean>(false); 
	isSig2 = signal<boolean>(false);
    isPin1 = signal<boolean>(false); 
	isPin2 = signal<boolean>(false);
	isNuv1 = signal<boolean>(false); 
	isNuv2 = signal<boolean>(false);
	showNuv1 = signal<boolean>(true); 
	showNuv2 = signal<boolean>(true);
	isNuv1NameInvalid = signal<boolean>(false);
	isNuv2NameInvalid = signal<boolean>(false);
	isOos1 = signal<boolean>(false); 
	isOos2 = signal<boolean>(false);
	showOos1 = signal<boolean>(true); 
	showOos2 = signal<boolean>(true);
	isMobile = signal<boolean>(false);
    mobileNuvInput1 = signal<boolean>(false);
    mobileNuvInput2 = signal<boolean>(false);
	// S2N-3076: a dual verification is never a write the View Only profile may make, so neither slot
	// offers a PIN pad, signature canvas, NUV or OOS input; slots already finalized still display.
	// Set in ngOnInit: sec is constructor-injected, so it is not available to a field initializer.
	isViewOnly = false;
	// #endregion

	faIcon = { faFileSignature, faUserNurse, faHouseMedicalCircleXmark };	

	// #region Gets
	// Getter functions for easier signature access
	get signature1(): SignatureInput { return this.model?.signature1; }
	get signature2(): SignatureInput { return this.model?.signature2; }

	// Getter functions based on verification settings to help in HTML
	/*******************************************************************/
	/*******************************************************************/
    get displayPin(): boolean { return ( this.model?.verificationSettings?.verificationTypes?.includes(VerificationTypeEnum.Pin) ); }
	get displaySignature(): boolean { return ( this.model?.verificationSettings?.verificationTypes?.includes(VerificationTypeEnum.Drawn) ); }
	get displayNuv(): boolean { 
        return ( 
            this.model?.verificationSettings?.verificationTypes?.includes(VerificationTypeEnum.Nuv) 
            && this.showNuvVerification
        ); 
    }
	get displayOos(): boolean { 
        return ( 
            this.model?.verificationSettings?.verificationTypes?.includes(VerificationTypeEnum.Oos) 
            && this.isUsingOos
        ); 
    }
    // True if only one signature box should be displayed
	get displayOneSigBox(): boolean { return this.model?.verificationSettings?.numberOfVerifications === NumberOfVerificationsEnum.One; }
	get displayNoSigBoxes(): boolean { return this.model?.verificationSettings?.verificationTypes?.length === 0; }
	// #endregion
	
    constructor(
        public gvs: GlobalVariablesService
		, private sec: SecurityService
		, private httpClient: HttpClient
		, private ms: SystemMessageService
		, private cdref: ChangeDetectorRef
		, private ns: NotificationService
        , private renderer: Renderer2
	) { }

	// Trigger signature events on document pointer up (so it will work even if they drag the pointer off the signature pad)
	@HostListener('document:pointerup') onPointerUp() {
		if (
			this.signaturePad1 
			&& !this.signaturePad1.isEmpty() 
			&& !this.signature1?.isFinalized
		) { this.emitSignatureEvent(1); }
		if (
			this.signaturePad2 
			&& !this.signaturePad2.isEmpty() 
			&& !this.signature2?.isFinalized
		) { this.emitSignatureEvent(2); }
		if (
			this.nuvSignaturePad1 
			&& !this.nuvSignaturePad1.isEmpty() 
			&& !this.signature1?.isFinalized
		) { this.emitNuvSignatureEvent(1); }
		if (
			this.nuvSignaturePad2 
			&& !this.nuvSignaturePad2.isEmpty() 
			&& !this.signature2?.isFinalized
		) { this.emitNuvSignatureEvent(2); }
	}

	@HostListener('window:resize', ['$event']) onResize(event: any) { 
		this.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); 
		this.resizeCanvas(this.canvas1, this.signaturePad1);
		this.resizeCanvas(this.canvas2, this.signaturePad2);
		this.resizeCanvas(this.nuvCanvas1, this.nuvSignaturePad1);
		this.resizeCanvas(this.nuvCanvas2, this.nuvSignaturePad2);
	}

    ngOnInit() { 
        this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));		
		this.user = this.sec.getUser();
		this.clientId = this.user?.clientId!;
		this.clientNonUserName = this.user?.clientNonUserName ?? "Non-User";
		this.isViewOnly = this.sec.isViewOnly();
	}

	ngOnChanges() {
		if (this.singleAuthIsOpen) {
			this.isSig1.set(false); 
			this.isSig2.set(false);
			this.isPin1.set(false); 
			this.isPin2.set(false);
			this.isNuv1.set(false); 
			this.isNuv2.set(false);
			this.showNuv1.set(true); 
			this.showNuv2.set(true);
			this.isOos1.set(false); 
			this.isOos2.set(false);
			this.showOos1.set(true); 
			this.showOos2.set(true);
		}
	}

	/** After the component view has been fully initialized, init the signature pad plugin */
	ngAfterViewInit() {
		this.signaturePad1 = this.initCanvas(this.canvas1)!;
		this.signaturePad2 = this.initCanvas(this.canvas2)!;

		this.nuvSignaturePad1 = this.initCanvas(this.nuvCanvas1)!;
		this.nuvSignaturePad2 = this.initCanvas(this.nuvCanvas2)!;

		this.onResize(null);

		if (
			this.signature1?.isFinalized 
			&& this.signature1?.verificationType === 4
		) { this.showNuv2.set(false); }
		if (
			this.signature2?.isFinalized 
			&& this.signature2?.verificationType === 4
		) { this.showNuv1.set(false); }

		if (this.displayOneSigBox) {
			this.showOos1.set(false);
			this.showOos2.set(false);
		}

		if (this.oosIsOffGoing) { this.showOos1.set(false); }
		else { this.showOos2.set(false); }

		this.cdref.detectChanges();
	}

	/** This ensures that the canvas will be properly initialized when it appears inside an ng-container or goes in and out of the DOM */
	ngAfterContentChecked() {
		if (
			!this.viewCheckInit() 
			&& this.canvas1?.nativeElement.offsetHeight > 0 
			&& this.canvas2?.nativeElement.offsetHeight > 0
			&& this.nuvCanvas1?.nativeElement.offsetHeight > 0 
			&& this.nuvCanvas2?.nativeElement.offsetHeight > 0 
		) {
			this.viewCheckInit.set(true);
			this.onResize(null);
		}
	}	

	clearPadNumbers(padNumber: number) {
		if (padNumber === 1) {
			this.isPin1.set(false); 
			this.isSig1.set(false);
			this.isNuv1.set(false);
		} else {
			this.isPin2.set(false); 
			this.isSig2.set(false);
			this.isNuv2.set(false);
		}
	}

	/**
	 * Used to get the title displayed above the signature boxes.
	 * Will depend on the allowed verification methods and number.
	 *
	 * @param num Will be 1 if referencing the signature pad on the left and 2 for the right
    */
	getTitle(num: number) { 
		if (this.isMedUse) {
			if (num === 1) { return this.gvs.clientAdministeredByName() ?? "Administered By:"; }
			else if (num === 2) { return this.gvs.clientWitnessedByName() ?? "Witnessed By:"; }
		}
		if (this.isWitness) { return `${this.appendTitle[num - 1]}Witness`}
		return `${this.appendTitle[num - 1]}Verification`; 
	}	

	saveEvent() {
		if (this.oosIsOffGoing) {
			this.model.oosIsOffGoing = this.oosIsOffGoing;
			this.model.oosName = this.oosOffGoingStr;
		}

		this.form.controls.dualAuthVm.patchValue(this.model);
		this.ms.getHttpObservable(this, this.saveUrl, this.form)
			.subscribe((sm: SystemMessage) => {
				if (sm.isSuccessful) {
					this.model.newSignatureNumber = null!;
					this.form.controls.dualAuthVm.patchValue(sm.model.dualAuthVm);
					this.saveReturn.emit(sm);
					this.isSig1.set(false);
					this.isPin1.set(false); 
					this.isSig2.set(false);
					this.isPin2.set(false); 
					this.isNuv1.set(false); 
					this.isNuv2.set(false);
					this.isOos1.set(false);
					this.isOos2.set(false);
				} else { this.unsuccessful.emit(sm); }
			}
		);
	}

	// #region Drawn signature
	resizeCanvas(
		canvas: ElementRef
		, signaturePad: SignaturePad
		, padNumber: number = 0
	) {
		if (canvas) {
			const ratio = window.devicePixelRatio;
            if (padNumber === 0) {
                canvas.nativeElement.width = canvas.nativeElement?.offsetWidth * ratio;
                canvas.nativeElement.height = canvas.nativeElement?.offsetHeight * ratio;
            } else {
                setTimeout(() => {
                    if (padNumber === 1) {
						if (canvas.nativeElement.id === 'signaturePad1') {
							canvas.nativeElement.width = this.container1.nativeElement.offsetWidth;
							canvas.nativeElement.height = this.container1.nativeElement.offsetHeight;
						} else if (canvas.nativeElement.id === 'nuvSignaturePad1') {
							canvas.nativeElement.width = this.nuvContainer1.nativeElement.offsetWidth;
							canvas.nativeElement.height = this.nuvContainer1.nativeElement.offsetHeight;
						}
                    } else if (canvas.nativeElement.id === 'signaturePad2') {
                        canvas.nativeElement.width = this.container2.nativeElement.offsetWidth;
                        canvas.nativeElement.height = this.container2.nativeElement.offsetHeight;
                    } else if (canvas.nativeElement.id === 'nuvSignaturePad2') {
                        canvas.nativeElement.width = this.nuvContainer2.nativeElement.offsetWidth;
                        canvas.nativeElement.height = this.nuvContainer2.nativeElement.offsetHeight;
                    }                    
                }, 50);
            }
			canvas.nativeElement.getContext('2d').scale(ratio, ratio);
			signaturePad?.clear();            
		}
	}

    initCanvas(el: ElementRef) {
		if (el) { return new SignaturePad(el.nativeElement); }
		return null;
	}

	/**
	 * S2N-1420 (5c): the first position is refused before it opens, not only hidden -- the same
	 * shape the redesigned stepper's openVerify(1) uses. The buttons are already gone in the
	 * template; this keeps a stray programmatic call from opening a pad the server will refuse.
	 */
	private cannotSign(padNumber: number): boolean {
		return this.isViewOnly
			|| (
				padNumber === 1
				&& !this.canSignFirst
			);
	}

	sigClick(padNumber: number) {
		if (this.cannotSign(padNumber)) { return; }
        if (padNumber === 1) {
			this.isSig1.set(true);
			this.clearPadNumbers(2);
            this.signaturePad1 = this.initCanvas(this.canvas1)!;
            this.resizeCanvas(this.canvas1, this.signaturePad1, 1);
        } else {
			this.isSig2.set(true);
			this.clearPadNumbers(1);
            this.signaturePad2 = this.initCanvas(this.canvas2)!;
            this.resizeCanvas(this.canvas2, this.signaturePad2, 2);
        }
		this.doubleAuthIsOpen.emit(true);
    }

	cancelSig(padNumber: number) {
		if (padNumber === 1) {
			this.isSig1.set(false);
			this.canvas1.nativeElement.width = 0;
			this.canvas1.nativeElement.height = 0;
			this.signaturePad1 = null!;
		} else {
			this.isSig2.set(false);
			this.canvas2.nativeElement.width = 0;
			this.canvas2.nativeElement.height = 0;
			this.signaturePad2 = null!;
		}
		this.doubleAuthIsOpen.emit(false);
	}	

	validateSig(padNumber: number) {
		if (!this.form.valid) { return; }
		this.model.newSignatureNumber = padNumber;
		this.model.verificationType = VerificationTypeEnum.Drawn;
		this.beforeSave.emit(padNumber);
		this.saveEvent();
		this.isSig1.set(false);
		this.isSig2.set(false);
	}

	/**
	 * This will emit an event on the signatureEvents object any time there is a change in the signature
	 *
	 * @param signatureNumber Will be 1 if referencing the signature pad on the left and 2 for the right
	 * @param signature The signature input that was received. Will be null if empty.
	 * @param isTwoFactor Whether or not this event is coming from a FIDO device
    */
	emitSignatureEvent(
		signatureNumber: number
		, signature: SignatureInput | undefined = undefined
		, isPin = false
		, isNuv = false
	) {
		// Get the correct signature pad based on signature number
		const signaturePad = signatureNumber === 1 
			? this.signaturePad1 
			: this.signaturePad2;
		
		// Get the new signature
		// * This is passed in for FIDO events
		// * Will be undefined for Drawn events, and use the default below
		// * Null indicates an empty signature
		signature = signature! ??
			(signature === null
				? null
				: {
					signatureId: 0
					, signatureBase64: signaturePad.toDataURL().replace('data:image/png;base64,', '')
					, signaturePath: ""
					, timeStamp: null
					, isPin
					, isNuv
					, userName: this.user.fullName
					, userId: this.user.id
					, isFinalized: false
					, verificationType: VerificationTypeEnum.Drawn
					, isOos: false
					, userSignatureId: this.user.userSignatureId
				  }
			);

		// Set the new signature
		let prevSignature: SignatureInput | null = null;
		if (signatureNumber === 1) {
			prevSignature = this.model.signature1;
			this.model.signature1 = signature;
		} else if (signatureNumber === 2) {
			prevSignature = this.model.signature2;
			this.model.signature2 = signature;
		}

		// Emit the event if the signature has changed
		if (prevSignature !== signature) {
			this.signatureEvents.emit(
                {
                    signatureNumber
                    , signature
                    , revert: () =>
                        this.emitSignatureEvent(
                            signatureNumber
                            , prevSignature!
                            , prevSignature?.isPin ?? false
                            , prevSignature?.isNuv ?? false
                        )
                }
            );
		}		
	}
	// #endregion

	// #region Pin
	pinClick(padNumber: number) {
		// S2N-1420 (5c): see cannotSign -- the first position is not a Witness's.
		if (this.cannotSign(padNumber)) { return; }
        if (padNumber === 1) {
            this.isPin1.set(true);
			this.clearPadNumbers(2);
			setTimeout(() => { 
				const element = (<HTMLInputElement>document.getElementById('pin1'));
				if (element !== null) { 
					element.value = "";
					element.focus(); 
				}
			}, 0);	
        } else {
			this.isPin2.set(true);
			this.clearPadNumbers(1);
			setTimeout(() => { 
				const element = (<HTMLInputElement>document.getElementById('pin2'));
				if (element !== null) { 
					element.value = "";
					element.focus(); 
				}
			}, 0);	
        }
		this.doubleAuthIsOpen.emit(true);
    }	

	cancelPin(padNumber: number) {
		if (padNumber === 1) { this.isPin1.set(false); } 
		else { this.isPin2.set(false); }
		this.doubleAuthIsOpen.emit(false);
	}

	/** S2N-1420: whether a Witness-profile PIN may satisfy this pad (see witnessEligiblePads). */
	padIsWitnessEligible(padNumber: number): boolean {
		const pads = this.witnessEligiblePads
			?? (this.isWitness ? [1, 2] : this.isMedUse ? [2] : []);
		return pads.includes(padNumber);
	}

	/**
	 * S2N-1420: whether the current profile may attempt this pad at all. An operational profile may
	 * attempt either; a restricted one (Witness) only a witness-eligible pad — the server refuses
	 * anything else as an invalid PIN, so the buttons are not offered in the first place.
	 */
	padIsAvailable(padNumber: number): boolean {
		return profileIsOperational(this.sec.getSecurity()?.permissionProfile)
			|| this.padIsWitnessEligible(padNumber);
	}

	validatePin(padNumber: number) {
        if (!this.form.valid) { return; }
        this.model.newSignatureNumber = padNumber;
		this.model.verificationType = VerificationTypeEnum.Pin;
		this.beforeSave.emit(true);

        let pin = "";
        if (padNumber === 1) { pin = this.pin1; }
        else { pin = this.pin2; }

		const user = this.sec.getUser();
		// S2N-1420: pre-flight hint mirroring the server's SignatureSlotMap — a Witness PIN is only
		// accepted for designated witness positions (the save path re-enforces this server-side)
		const witnessEligible = this.padIsWitnessEligible(padNumber);
		this.httpClient.get(`api/Admin/Users/VerifyPIN/?userId=${user?.id}&pin=${pin}&witnessEligible=${witnessEligible}`)
			.subscribe((msg: any) => {
				if (msg.isSuccessful) {
					this.model.pin = pin;
					this.model.userSignatureId = msg.model.id;
					this.model.newSignatureNumber = padNumber;
					this.saveEvent();
				} else { this.ns.showError(msg.message, { timeOut: 5000 }); }
			}
		); 
	}
	// #endregion

	// #region Out of Service
	getOosSelectItems(event: SelectItem[]) { this.oosSelectItems = event; }

	oosClick(padNumber: number) {
		// S2N-1420 (5c): see cannotSign -- the first position is not a Witness's.
		if (this.cannotSign(padNumber)) { return; }
		this.oosId = null!;
        if (padNumber === 1) { this.isOos1.set(true); }
		else {this.isOos2.set(true); }
		this.doubleAuthIsOpen.emit(true);
    }

	cancelOos(padNumber: number) {
		if (padNumber === 1) { this.isOos1.set(false); } 
		else { this.isOos2.set(false); }
		this.doubleAuthIsOpen.emit(false);
	}

	validateOos(padNumber: number) { 
		if (!this.form.valid) { return; }
		if (this.oosId == null) { 
			this.ns.showError("No " + this.clientOutOfServiceName + " Location Selected", { timeOut: 5000 });
			return;
		}

        this.model.newSignatureNumber = padNumber;
		this.model.verificationType = VerificationTypeEnum.Oos;
		this.model.oosIsOffGoing = this.oosIsOffGoing;
		this.beforeSave.emit(true);

		// Get the Out of Service Location Name from the id
		this.oosSelectItems.forEach((item) => {	if (item.value === this.oosId) { this.model.oosName = item.text; } });

		this.saveEvent();
		this.oosId = null!;
	}
	// #endregion

	// #region Nurse Verification
	nuvClick(padNumber: number) {
		// S2N-1420 (5c): see cannotSign -- the first position is not a Witness's.
		if (this.cannotSign(padNumber)) { return; }
        if (
            this.isMedUse
            && this.isMobile()
        ) {
            const divMedUseHeader = document.getElementById('divMedUseHeader');
            const divMedUseSection1 = document.getElementById('divMedUseSection1');
            if (
                divMedUseHeader 
                && divMedUseSection1
            ) {
                this.renderer.setStyle(divMedUseHeader, 'display', 'none');
                this.renderer.setStyle(divMedUseSection1, 'display', 'none');
            }
        }
        
		if (padNumber === 1) {
            this.isNuv1.set(true);
            if (this.isMedUse) { this.mobileNuvInput1.set(true); }
			this.clearPadNumbers(2);
            if (!this.isMedUse) {
                this.nuvSignaturePad1 = this.initCanvas(this.nuvCanvas1)!;
                this.resizeCanvas(this.nuvCanvas1, this.nuvSignaturePad1, 1);
            }			
		} else {
			this.isNuv2.set(true);
            if (this.isMedUse) { this.mobileNuvInput2.set(true); }
			this.clearPadNumbers(1);
            if (!this.isMedUse) {
    			this.nuvSignaturePad2 = this.initCanvas(this.nuvCanvas2)!;
                this.resizeCanvas(this.nuvCanvas2, this.nuvSignaturePad2, 2);
            }
		}
		this.doubleAuthIsOpen.emit(true);
	}

    mobileNuvInput(padNumber: number) {
        if (padNumber === 1) {
            if (FormatHelperService.GetIsNullyOrWhitespace(this.nonUserName1)) { 
                this.mobileNuvInput1.set(true);
				this.ns.showError("Typed Name required", { timeOut: 5000 });
				return; 
			} else if (this.nonUserName1?.length > 256) { 
                this.mobileNuvInput1.set(true);
				this.ns.showError("Name cannot be more than 256 characters", { timeOut: 5000 });
				return;
			}
            setTimeout(() => {
                this.mobileNuvInput1.set(false);
                this.nuvSignaturePad1 = this.initCanvas(this.nuvCanvas1)!;
                this.resizeCanvas(this.nuvCanvas1, this.nuvSignaturePad1, 1);
            }, 0);
        } else {
            if (FormatHelperService.GetIsNullyOrWhitespace(this.nonUserName2)) { 
                this.mobileNuvInput2.set(true);
				this.ns.showError("Typed Name required", { timeOut: 5000 });
				return; 
			} else if (this.nonUserName2?.length > 256) { 
                this.mobileNuvInput2.set(true);
				this.ns.showError("Name cannot be more than 256 characters", { timeOut: 5000 });
				return;
			}
            this.mobileNuvInput2.set(false);
			this.nuvSignaturePad2 = this.initCanvas(this.nuvCanvas2)!;
            this.resizeCanvas(this.nuvCanvas2, this.nuvSignaturePad2, 2);
            setTimeout(() => {
                this.mobileNuvInput2.set(false);
                this.nuvSignaturePad2 = this.initCanvas(this.nuvCanvas2)!;
                this.resizeCanvas(this.nuvCanvas2, this.nuvSignaturePad2, 2);
            }, 0);
        }
    }

	cancelNuvSig(padNumber: number) {
        if (
            this.isMedUse 
            && this.isMobile()
        ) {
            const divMedUseHeader = document.getElementById('divMedUseHeader');
            const divMedUseSection1 = document.getElementById('divMedUseSection1');
            if (
                divMedUseHeader 
                && divMedUseSection1
            ) {
                this.renderer.setStyle(divMedUseHeader, 'display', 'block');
                this.renderer.setStyle(divMedUseSection1, 'display', 'block');
            }
        }

		if (padNumber === 1) {
            this.mobileNuvInput1.set(false);
			this.isNuv1.set(false);
			this.nuvCanvas1.nativeElement.width = 0;
			this.nuvCanvas1.nativeElement.height = 0;
			this.nuvSignaturePad1 = null!;
            this.signature1.verificationType = null!;
            this.nonUserName1 = "";
		} else {
            this.mobileNuvInput2.set(false);
			this.isNuv2.set(false);
			this.nuvCanvas2.nativeElement.width = 0;
			this.nuvCanvas2.nativeElement.height = 0;
			this.nuvSignaturePad2 = null!;
            this.signature2.verificationType = null!;
            this.nonUserName2 = "";
		}
		this.doubleAuthIsOpen.emit(false);
	}

	validateNuvSig(padNumber: number) {
		if (!this.form.valid) { return; }
		const sig = new SignatureInput();
		sig.signatureId = 0;
		sig.userSignatureId = null!;
		sig.isOos = false;
		sig.signaturePath = "";
		sig.userId = null!;
		sig.timeStamp = null!;
		sig.verificationType = VerificationTypeEnum.Nuv;
		sig.isFinalized = false;
		sig.isPin = false;
		sig.isNuv = true;

		if (padNumber === 1) {
			if (this.nuvSignaturePad1.isEmpty()) { 
                this.mobileNuvInput1.set(false);
				this.ns.showError("Signature required", { timeOut: 5000 });
				return; 
			}
            if (FormatHelperService.GetIsNullyOrWhitespace(this.nonUserName1)) { 
                this.mobileNuvInput1.set(true);
				this.ns.showError("Typed Name required", { timeOut: 5000 });
				return; 
			} else if (this.nonUserName1?.length > 256) { 
                this.mobileNuvInput1.set(true);
				this.ns.showError("Name cannot be more than 256 characters", { timeOut: 5000 });
				return;
			}
			sig.signatureBase64 = this.nuvSignaturePad1.toDataURL().replace('data:image/png;base64,', '');
			sig.userName = this.nonUserName1;
			this.model.signature1 = sig;
			this.model.signature1.isNuv = true;
			this.model.signature1.userName = this.nonUserName1;
		} else {
            if (this.nuvSignaturePad2.isEmpty()) { 
                this.mobileNuvInput2.set(false);
				this.ns.showError("Signature required", { timeOut: 5000 });
				return; 
			}
			if (FormatHelperService.GetIsNullyOrWhitespace(this.nonUserName2)) { 
                this.mobileNuvInput2.set(true);
				this.ns.showError("Typed Name required", { timeOut: 5000 });
				return; 
			} else if (this.nonUserName2?.length > 256) { 
                this.mobileNuvInput2.set(true);
				this.ns.showError("Name cannot be more than 256 characters", { timeOut: 5000 });
				return;
			}
			sig.signatureBase64 = this.nuvSignaturePad2.toDataURL().replace('data:image/png;base64,', '');
			sig.userName = this.nonUserName2;
			this.model.signature2 = sig;
			this.model.signature2.isNuv = true;
			this.model.signature2.userName = this.nonUserName2;
		}
		
		this.model.newSignatureNumber = null!;
		this.model.verificationType = VerificationTypeEnum.Nuv;
		this.beforeSave.emit(true);
		this.saveNuvSignature(padNumber);
	}

	saveNuvSignature(padNumber: number) {
		this.form.controls.dualAuthVm.patchValue(this.model);
		this.ms.getHttpObservable(this, this.saveUrl, this.form)
			.subscribe((sm: SystemMessage) => {
				if (sm.isSuccessful) {
					this.model.newSignatureNumber = null!;
					this.form.controls.dualAuthVm.patchValue(sm.model.dualAuthVm);
					this.saveReturn.emit(sm);
					this.isSig1.set(false); 
					this.isSig2.set(false);
					this.isPin1.set(false); 
					this.isPin2.set(false); 
					this.isNuv1.set(false); 
					this.isNuv2.set(false);
					if (padNumber === 1) { this.showNuv2.set(false); }
					else { this.showNuv1.set(false); }
                    this.cancelNuvSig(padNumber);
				} else { this.unsuccessful.emit(sm); }
			}
		);
	}

	/**
	 * This will emit an event on the signatureEvents object any time there is a change in the signature
	 *
	 * @param signatureNumber Will be 1 if referencing the signature pad on the left and 2 for the right
	 * @param signature The signature input that was received. Will be null if empty.
	 * @param isTwoFactor Whether or not this event is coming from a FIDO device
	 */
	emitNuvSignatureEvent(
		signatureNumber: number
		, signature: SignatureInput | undefined = undefined
		, isPin = false
		, isNuv = true
		, userName = ""
	) {
		// Get the correct signature pad based on signature number
		const nuvSignaturePad = signatureNumber === 1 
			? this.nuvSignaturePad1 
			: this.nuvSignaturePad2;

		// Get the new signature
		// Will be undefined for Drawn events, and use the default below
		// Null indicates an empty signature
		signature = signature! ??
			(signature === null
				? null
				: {
					signatureId: 0
					, signatureBase64: nuvSignaturePad.toDataURL().replace('data:image/png;base64,', '')
					, signaturePath: ""
					, timeStamp: null
					, isPin
					, isNuv
					, userName
					, userId: this.user.id
					, isFinalized: false
					, verificationType: VerificationTypeEnum.Nuv
					, isOos: false
					, userSignatureId: this.user.userSignatureId
				  }
			);

		// Set the new signature
		let prevSignature: SignatureInput | null = null;
		if (signatureNumber === 1) {
			prevSignature = this.model.signature1;
			this.model.signature1 = signature;
		} else if (signatureNumber === 2) {
			prevSignature = this.model.signature2;
			this.model.signature2 = signature;
		}

		// Emit the event if the signature has changed
		if (prevSignature !== signature) {
			this.signatureEvents.emit(
                {
                    signatureNumber
                    , signature
                    , revert: () =>
                        this.emitNuvSignatureEvent(
                            signatureNumber
                            , prevSignature!
                            , prevSignature?.isPin ?? false
                            , prevSignature?.isNuv ?? true
                            , prevSignature?.userName ?? ""
                        )
                }
            );
		}
	}

	validateNurseVerificationName(
        event: any
        , whichOne: number
    ) {
		if (whichOne === 1) { this.isNuv1NameInvalid.set(event.target.value?.length > 256); } 
		else if (whichOne === 2) { this.isNuv2NameInvalid.set(event.target.value?.length > 256); }
	}
	// #endregion
}
